import React, { useState, useEffect } from 'react';
import { supabase } from '../store/supabase';
// eslint-disable-next-line no-unused-vars
import { customerStore, serviceRecordStore, serviceRequestStore, referralStore, promotionStore, passportStore, vehicleStore } from '../store/data';
import {
  LayoutDashboard, Users, Bike, ClipboardList, Gift,
  Search, Plus, X, ChevronLeft, BarChart2, QrCode,
  Tag, Edit2, Trash2, Shield
} from 'lucide-react';
import logo from '../logo.png';
import { QRCodeSVG } from 'qrcode.react';

const MUNICIPALITIES = [
  'Alcantara', 'Banton', 'Cajidiocan', 'Calatrava', 'Concepcion',
  'Corcuera', 'Ferrol', 'Looc', 'Magdiwang', 'Odiongan',
  'Romblon', 'San Agustin', 'San Andres', 'San Fernando',
  'San Jose', 'Santa Fe', 'Santa Maria'
];
const SERVICE_TYPES = ['Regular Check-up', 'Battery Service', 'Brake Repair', 'Tire Service', 'Motor Repair', 'Electrical Repair', 'Full Overhaul', 'Warranty Service', 'Others'];

function StatusBadge({ status }) {
  const cfg = {
    pending: ['badge-yellow', 'Pending'],
    scheduled: ['badge-blue', 'Scheduled'],
    completed: ['badge-green', 'Completed'],
    cancelled: ['badge-gray', 'Cancelled'],
    active: ['badge-green', 'Active'],
    expired: ['badge-red', 'For Renewal'],
    expiring: ['badge-yellow', 'Expiring Soon'],
    successful: ['badge-green', 'Successful'],
    rewarded: ['badge-blue', 'Rewarded'],
  };
  const [cls, label] = cfg[status] || ['badge-gray', status];
  return <span className={cls}>{label}</span>;
}

export default function AdminDashboard({ onBack, onUnitAccess }) {
  const [tab, setTab] = useState('dashboard');
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [passports, setPassports] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [showAddPromo, setShowAddPromo] = useState(false);
  const [editPromo, setEditPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({ title: '', description: '', category: 'Service', validUntil: '' });
  const [editCustomer, setEditCustomer] = useState(null);
  const [editCustomerForm, setEditCustomerForm] = useState({});
  const [qrCustomer, setQrCustomer] = useState(null);
  const [toast, setToast] = useState('');

  const [customerForm, setCustomerForm] = useState({ name: '', mobile: '', municipality: 'Romblon', email: '' });
  const [vehicleForm, setVehicleForm] = useState({ customerId: '', model: '', brand: 'SEVENSTARS', serialNumber: '', purchaseDate: '', warrantyMonths: '3', batteryType: 'Lead Acid', color: '' });
  const [serviceForm, setServiceForm] = useState({ vehicleId: '', customerId: '', date: new Date().toISOString().split('T')[0], type: 'Regular Check-up', technicianNotes: '', partsReplaced: '', nextServiceDate: '', technicianName: '' });

  const loadAll = async () => {
    const [custs, vehs, reqs, refs, recs, promos, passes] = await Promise.all([
      customerStore.getAll(),
      vehicleStore.getAll(),
      serviceRequestStore.getAll(),
      referralStore.getAll(),
      serviceRecordStore.getAll(),
      promotionStore.getAll(),
      passportStore.getAll(),
    ]);
    setCustomers(custs);
    setVehicles(vehs);
    setServiceRequests(reqs);
    setReferrals(refs);
    setServiceRecords(recs);
    setPromotions(promos);
    setPassports(passes);
  };

  useEffect(() => { loadAll(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  // Delete all test records (service requests, referrals, service records)
  const clearTestData = async () => {
    if (!window.confirm('Delete ALL service requests, referrals, and service records?\n\nCustomers and passports will NOT be deleted.\n\nThis cannot be undone.')) return;
    await supabase.from('service_requests').delete().neq('id', 'x');
    await supabase.from('referrals').delete().neq('id', 'x');
    await supabase.from('service_records').delete().neq('id', 'x');
    await loadAll();
    showToast('✅ Test records cleared.');
  };

  const handleSearch = (q) => {
    setSearchQ(q);
    if (q.length >= 2) customerStore.searchAdmin(q).then(setSearchResults);
    else setSearchResults(null);
  };

  const addCustomer = async () => {
    if (!customerForm.name || !customerForm.mobile) { showToast('Name and mobile are required.'); return; }
    await customerStore.add(customerForm);
    customerStore.getAll().then(setCustomers);
    setShowAddCustomer(false);
    setCustomerForm({ name: '', mobile: '', municipality: 'Romblon', email: '' });
    showToast('Customer added!');
  };

  const addVehicle = async () => {
    if (!vehicleForm.customerId || !vehicleForm.model || !vehicleForm.serialNumber) {
      showToast('Customer, model, and serial number are required.'); return;
    }
    const newV = await vehicleStore.add(vehicleForm);
    // V3: Auto-generate Passport ID, Unit ID, Warranty ID and QR token
    await passportStore.register(newV.id, vehicleForm.customerId);
    vehicleStore.getAll().then(setVehicles);
    passportStore.getAll().then(setPassports);
    setShowAddVehicle(false);
    setVehicleForm({ customerId: '', model: '', brand: 'SEVENSTARS', serialNumber: '', purchaseDate: '', warrantyMonths: '3', batteryType: 'Lead Acid', color: '' });
    showToast('Unit registered! Passport ID & QR generated. Go to Passports tab to view.');
  };

  const addServiceRecord = async () => {
    if (!serviceForm.vehicleId || !serviceForm.type) { showToast('Vehicle and service type required.'); return; }
    await serviceRecordStore.add(serviceForm);
    serviceRecordStore.getAll().then(setServiceRecords);
    setShowAddService(false);
    setServiceForm({ vehicleId: '', customerId: '', date: new Date().toISOString().split('T')[0], type: 'Regular Check-up', technicianNotes: '', partsReplaced: '', nextServiceDate: '', technicianName: '' });
    showToast('Service record added!');
  };

  const savePromo = async () => {
    if (!promoForm.title || !promoForm.description) { showToast('Title and description required.'); return; }
    if (editPromo) {
      await promotionStore.update(editPromo.id, promoForm);
      showToast('Promo updated!');
    } else {
      await promotionStore.add(promoForm);
      showToast('Promo added!');
    }
    promotionStore.getAll().then(setPromotions);
    setShowAddPromo(false);
    setEditPromo(null);
    setPromoForm({ title: '', description: '', category: 'Service', validUntil: '' });
  };

  const deletePromo = (id) => {
    promotionStore.delete(id);
    promotionStore.getAll().then(setPromotions);
    showToast('Promo deleted.');
  };

  const openEditCustomer = (c) => {
    setEditCustomer(c);
    setEditCustomerForm({ name: c.name, mobile: c.mobile || '', email: c.email || '', municipality: c.municipality || '' });
  };

  const saveCustomerEdit = async () => {
    if (!editCustomerForm.name) { showToast('Name is required.'); return; }
    await customerStore.update(editCustomer.id, editCustomerForm);
    customerStore.getAll().then(setCustomers);
    setEditCustomer(null);
    showToast('Customer updated!');
  };

  // Dashboard stats
  const openRequests = serviceRequests.filter(r => r.status === 'pending' || r.status === 'scheduled').length;
  const issueCounts = {};
  serviceRequests.forEach(r => { issueCounts[r.issue_category] = (issueCounts[r.issue_category] || 0) + 1; });
  const topIssue = Object.entries(issueCounts).sort((a, b) => b[1] - a[1])[0];
  const muniCounts = {};
  customers.forEach(c => { muniCounts[c.municipality] = (muniCounts[c.municipality] || 0) + 1; });

  const tabs = [
    { key: 'dashboard', label: 'Overview', icon: LayoutDashboard },
    { key: 'passports', label: 'Passports', icon: Shield },
    { key: 'customers', label: 'Customers', icon: Users },
    { key: 'requests', label: 'Requests', icon: ClipboardList },
    { key: 'promos', label: 'Promos', icon: Tag },
    { key: 'referrals', label: 'Referrals', icon: Gift },
  ];

  const displayList = searchResults !== null ? searchResults : customers;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col max-w-md mx-auto">
      {/* Header */}
      <div className="px-5 pt-8 pb-5" style={{ background: 'linear-gradient(135deg, #081d31 0%, #1B4F72 100%)' }}>
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="text-slate-400 hover:text-white flex items-center gap-1 text-sm">
            <ChevronLeft size={16} /> Exit
          </button>
          <div className="flex items-center gap-2">
            <img src={logo} alt="SevenStars" className="h-5 w-auto object-contain" style={{filter:'brightness(0) invert(1)'}} />
            <span className="text-white text-sm font-semibold">Admin</span>
          </div>
        </div>
        <h1 className="text-white text-xl font-bold">SmartCare Dashboard</h1>
        <p className="text-slate-400 text-sm">Admin · E-Bike Romblon</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-24">

        {tab === 'dashboard' && (
          <div className="p-4 space-y-4">
            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Total Customers', value: customers.length, color: 'bg-brand-500' /* navy */, icon: Users },
                { label: 'Total Vehicles', value: vehicles.length, color: 'bg-blue-500', icon: Bike },
                { label: 'Open Requests', value: openRequests, color: 'bg-accent-500', icon: ClipboardList },
                { label: 'Referrals', value: referrals.length, color: 'bg-purple-500', icon: Gift },
              ].map(s => (
                <div key={s.label} className="card">
                  <div className={`w-10 h-10 ${s.color} rounded-xl flex items-center justify-center mb-3`}>
                    <s.icon size={18} className="text-white" />
                  </div>
                  <div className="text-3xl font-bold text-slate-800">{s.value}</div>
                  <div className="text-slate-500 text-xs mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Most common issue */}
            {topIssue && (
              <div className="card">
                <div className="flex items-center gap-2 mb-2">
                  <BarChart2 size={16} className="text-accent-500" />
                  <h3 className="font-bold text-slate-700">Top Issue Category</h3>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-700 font-medium">{topIssue[0]}</span>
                  <span className="badge-yellow">{topIssue[1]} requests</span>
                </div>
              </div>
            )}

            {/* Customers by municipality */}
            <div className="card">
              <h3 className="font-bold text-slate-700 mb-3">Customers by Municipality</h3>
              <div className="space-y-2">
                {Object.entries(muniCounts).sort((a, b) => b[1] - a[1]).map(([muni, count]) => (
                  <div key={muni} className="flex items-center gap-2">
                    <div className="flex-1 text-sm text-slate-600">{muni}</div>
                    <div className="flex-1 bg-slate-100 rounded-full h-2">
                      <div className="bg-brand-500 h-2 rounded-full" style={{ width: `${(count / customers.length) * 100}%` }} />
                    </div>
                    <span className="text-sm font-medium text-slate-700 w-5 text-right">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent requests */}
            <div className="card">
              <h3 className="font-bold text-slate-700 mb-3">Recent Service Requests</h3>
              {serviceRequests.slice(0, 3).map(r => (
                <div key={r.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <div>
                    <div className="text-sm font-medium text-slate-700">{r.name}</div>
                    <div className="text-xs text-slate-400">{r.issue_category} · {r.municipality}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setShowAddCustomer(true)}
                className="card flex flex-col items-center gap-2 py-4 hover:bg-brand-50 hover:border-brand-200 transition-all text-center active:scale-95">
                <Plus size={20} className="text-brand-500" />
                <span className="text-xs font-semibold text-slate-600">Add Customer</span>
              </button>
              <button onClick={() => setShowAddVehicle(true)}
                className="card flex flex-col items-center gap-2 py-4 hover:bg-blue-50 hover:border-blue-200 transition-all text-center active:scale-95">
                <Bike size={20} className="text-blue-500" />
                <span className="text-xs font-semibold text-slate-600">Add Vehicle</span>
              </button>
              <button onClick={() => setShowAddService(true)}
                className="card flex flex-col items-center gap-2 py-4 hover:bg-slate-100 transition-all text-center active:scale-95">
                <ClipboardList size={20} className="text-slate-500" />
                <span className="text-xs font-semibold text-slate-600">Add Record</span>
              </button>
              <button onClick={clearTestData}
                className="card flex flex-col items-center gap-2 py-4 hover:bg-red-50 transition-all text-center active:scale-95 border-red-100">
                <Trash2 size={20} className="text-red-400" />
                <span className="text-xs font-semibold text-red-400">Clear Tests</span>
              </button>
            </div>
          </div>
        )}

        {/* ── PASSPORTS TAB ─────────────────────────────── */}
        {tab === 'passports' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-800">Unit Passports</h2>
              <span className="text-sm text-slate-500">{passports.length} units</span>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Total Units', val: passports.length, color: '#1B4F72' },
                { label: 'Activated', val: passports.filter(p => p.is_activated).length, color: '#16a34a' },
                { label: 'Pending', val: passports.filter(p => !p.is_activated).length, color: '#E87722' },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-2xl p-3 text-center shadow-sm border border-slate-100">
                  <div className="text-2xl font-bold" style={{ color: s.color }}>{s.val}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Register new unit */}
            <button onClick={() => setShowAddVehicle(true)}
              className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 px-6 rounded-xl transition-all"
              style={{ background: '#1B4F72' }}>
              <Plus size={16} /> Register New Unit & Generate Passport
            </button>

            {/* Passport list */}
            <div className="space-y-3">
              {passports.slice(0, 30).map(p => {
                const v = vehicles.find(x => x.id === p.vehicle_id);
                const c = customers.find(x => x.id === p.customer_id);
                const qrUrl = `${window.location.origin}${window.location.pathname}?unit=${p.token}`;
                return (
                  <div key={p.id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-bold text-slate-800 text-sm">{p.passport_id}</span>
                          {p.is_activated
                            ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">✓ Activated</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Pending</span>
                          }
                        </div>
                        <div className="text-xs text-slate-500 space-y-0.5">
                          <div>Unit ID: <span className="font-medium text-slate-700">{p.unit_id}</span></div>
                          <div>Warranty: <span className="font-medium text-slate-700">{p.warranty_id}</span></div>
                          {v && <div>Model: <span className="font-medium text-slate-700">{v.model} · {v.color}</span></div>}
                          {c && <div>Owner: <span className="font-medium text-slate-700">{c.name}</span></div>}
                          {v && <div>Purchase Date: <span className="font-medium text-slate-700">{v.purchase_date}</span></div>}
                        </div>
                        <div className="flex gap-2 mt-3 flex-wrap">
                          <button
                            onClick={() => { onUnitAccess && onUnitAccess(p.token); }}
                            className="text-xs px-3 py-1.5 rounded-lg font-medium text-white"
                            style={{ background: '#1B4F72' }}>
                            View
                          </button>
                          <button
                            onClick={() => setQrCustomer({ ...p, name: c?.name || p.passport_id, municipality: c?.municipality || '' })}
                            className="text-xs px-3 py-1.5 rounded-lg font-medium border"
                            style={{ borderColor: '#1B4F72', color: '#1B4F72' }}>
                            <QrCode size={11} className="inline mr-1" />QR
                          </button>
                          <button
                            onClick={async () => {
                              const tempPin = await passportStore.generateTempPin(p.token);
                              alert(`Temp PIN for ${c?.name || p.passport_id}:\n\n${tempPin}\n\nValid for 24 hours.\nGive this to the customer to log in and set their own PIN.`);
                            }}
                            className="text-xs px-3 py-1.5 rounded-lg font-medium border"
                            style={{ borderColor: '#E87722', color: '#E87722' }}>
                            Temp PIN
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Reset passport for ${c?.name || p.passport_id}?\n\nThis will clear their PIN and they will need to re-activate.`)) {
                                await passportStore.resetPin(p.token);
                                loadAll();
                                showToast('Passport reset. Customer must re-activate.');
                              }
                            }}
                            className="text-xs px-3 py-1.5 rounded-lg font-medium border border-red-200 text-red-500">
                            Reset
                          </button>
                        </div>
                      </div>
                      <div className="bg-white p-1.5 rounded-xl border border-slate-100 ml-3 flex-shrink-0">
                        <QRCodeSVG value={qrUrl} size={52} fgColor="#1B4F72" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'customers' && (
          <div className="p-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <input
                className="input-field pl-10"
                placeholder="Search name, mobile, municipality..."
                value={searchQ}
                onChange={e => handleSearch(e.target.value)}
              />
              <Search size={16} className="absolute left-3 top-3.5 text-slate-400" />
              {searchQ && <button onClick={() => { setSearchQ(''); setSearchResults(null); }} className="absolute right-3 top-3.5"><X size={16} className="text-slate-400" /></button>}
            </div>

            <button onClick={() => setShowAddCustomer(true)} className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 px-6 rounded-xl transition-all active:scale-95 shadow-sm" style={{ backgroundColor: '#1B4F72' }}>
              <Plus size={16} /> Add New Customer
            </button>

            {displayList.length === 0 ? (
              <div className="card text-center py-8">
                <Users size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400">No customers found.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {displayList.map(c => {
                  const cv = vehicles.filter(v => v.customer_id === c.id);
                  return (
                    <button key={c.id} onClick={() => setSelectedCustomer(c.id === selectedCustomer ? null : c.id)}
                      className={`card w-full text-left transition-all ${selectedCustomer === c.id ? 'border-brand-300 bg-brand-50' : 'hover:border-slate-200'}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-bold text-slate-800">{c.name}</div>
                          <div className="text-sm text-slate-500 mt-0.5">{c.mobile || 'No mobile'} · {c.municipality}</div>
                          {cv.length > 0 && <div className="text-xs text-brand-600 mt-1">{cv[0].model} · {cv[0].serial_number}</div>}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {cv.length > 0 && <StatusBadge status={vehicleStore.getWarrantyInfo(cv[0]).status} />}
                          <div className="flex gap-1">
                            <button
                              onClick={e => { e.stopPropagation(); openEditCustomer(c); }}
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-all" style={{ backgroundColor: '#eef3f8', color: '#1B4F72' }}>
                              <Edit2 size={12} /> Edit
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); setQrCustomer(c); }}
                              className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-all" style={{ backgroundColor: '#eef3f8', color: '#1B4F72' }}>
                              <QrCode size={12} /> QR
                            </button>
                          </div>
                        </div>
                      </div>
                      {selectedCustomer === c.id && (
                        <div className="mt-3 pt-3 border-t border-brand-100 space-y-2">
                          {cv.map(v => {
                            const wi = vehicleStore.getWarrantyInfo(v);
                            return (
                              <div key={v.id} className="bg-white rounded-xl p-3 text-xs">
                                <div className="font-semibold text-slate-700">{v.model}</div>
                                <div className="text-slate-500 mt-0.5">SN: {v.serialNumber} · Battery: {v.batteryType}</div>
                                <div className="flex justify-between mt-1">
                                  <span className="text-slate-400">Warranty ends: {wi.endDate}</span>
                                  <StatusBadge status={wi.status} />
                                </div>
                                <div className="mt-2">
                                  <select
                                    className="text-xs border border-slate-200 rounded-lg px-2 py-1"
                                    value={wi.status}
                                    onChange={e => {
                                      vehicleStore.update(v.id, { warrantyStatus: e.target.value });
                                      loadAll();
                                    }}
                                  >
                                    <option value="active">Active</option>
                                    <option value="expiring">Expiring</option>
                                    <option value="expired">Expired</option>
                                  </select>
                                </div>
                              </div>
                            );
                          })}
                          {serviceRecords.filter(r => r.customerId === c.id).length > 0 && (
                            <div className="text-xs text-slate-500 mt-1">
                              {serviceRecords.filter(r => r.customerId === c.id).length} service record(s)
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'requests' && (
          <div className="p-4 space-y-4">
            <h2 className="section-title">Service Requests</h2>
            {serviceRequests.length === 0 ? (
              <div className="card text-center py-8">
                <ClipboardList size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400">No service requests yet.</p>
              </div>
            ) : (
              serviceRequests.sort((a,b) => (b.submitted_at||'').localeCompare(a.submitted_at||'')).map(r => (
                <div key={r.id} className="card">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-bold text-slate-800">{r.passport_id || r.unit_model || '—'}</div>
                      <div className="text-sm text-slate-500">{r.unit_model} · {r.contact}</div>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-sm text-slate-600 mb-1">
                    <span className="font-medium">{r.issue_category}:</span> {r.description}
                  </div>
                  <div className="text-xs text-slate-400 mb-1">Visit: {r.visit_date} · Submitted: {r.submitted_at}</div>
                  {r.photo_urls && <div className="text-xs text-blue-500 mb-2">📷 {r.photo_urls.split(',').filter(Boolean).length} photo(s) attached</div>}
                  <div className="flex gap-2 flex-wrap">
                    {['pending', 'scheduled', 'completed', 'cancelled'].map(s => (
                      <button key={s} onClick={() => { serviceRequestStore.updateStatus(r.id, s).then(() => loadAll()); }}
                        className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-all ${r.status === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'referrals' && (
          <div className="p-4 space-y-4">
            <h2 className="section-title">Referrals</h2>
            {referrals.length === 0 ? (
              <div className="card text-center py-8">
                <Gift size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400">No referrals yet.</p>
              </div>
            ) : (
              referrals.map(r => (
                <div key={r.id} className="card">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Referred by</div>
                      <div className="font-bold text-slate-800">{r.referrer_name || "—"}</div>
                    </div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 text-sm mb-3">
                    <div className="font-medium text-slate-700">{r.buyer_name}</div>
                    <div className="text-slate-500">{r.buyer_contact}</div>
                  </div>
                  <div className="flex gap-2">
                    {['pending', 'successful', 'rewarded'].map(s => (
                      <button key={s} onClick={() => { referralStore.updateStatus(r.id, s).then(() => loadAll()); }}
                        className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-all ${r.status === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'promos' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="section-title mb-0">Promotions</h2>
              <button onClick={() => { setEditPromo(null); setPromoForm({ title: '', description: '', category: 'Service', validUntil: '' }); setShowAddPromo(true); }}
                className="flex items-center gap-1 text-sm font-semibold text-white px-3 py-2 rounded-xl transition-all" style={{ backgroundColor: '#1B4F72' }}>
                <Plus size={14} /> Add Promo
              </button>
            </div>
            {promotions.length === 0 ? (
              <div className="card text-center py-8">
                <Tag size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400">No promotions yet.</p>
              </div>
            ) : (
              promotions.map(p => {
                const today = new Date().toISOString().split('T')[0];
                const expired = p.validUntil && p.validUntil < today;
                return (
                  <div key={p.id} className={`card ${!p.active || expired ? 'opacity-60' : ''}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="font-bold text-slate-800">{p.title}</div>
                        <span className="badge-gray text-xs mt-1">{p.category}</span>
                      </div>
                      <div className="flex gap-2 ml-2">
                        <button onClick={() => { setEditPromo(p); setPromoForm({ title: p.title, description: p.description, category: p.category, validUntil: p.validUntil || '' }); setShowAddPromo(true); }}
                          className="p-1.5 rounded-lg hover:bg-slate-100 transition-all">
                          <Edit2 size={14} className="text-slate-500" />
                        </button>
                        <button onClick={() => deletePromo(p.id)}
                          className="p-1.5 rounded-lg hover:bg-red-50 transition-all">
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{p.description}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className={expired ? 'text-red-500 font-medium' : 'text-slate-400'}>
                        Valid until: {p.validUntil || 'No expiry'} {expired ? '⚠ Expired' : ''}
                      </span>
                      <button onClick={() => { promotionStore.update(p.id, { active: !p.active }); loadAll(); }}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${p.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {p.active ? '✓ Active' : 'Inactive'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Edit Customer Modal */}
      {editCustomer && (
        <Modal title="Edit Customer" onClose={() => setEditCustomer(null)}>
          <div className="space-y-3">
            <Field label="Full Name *" value={editCustomerForm.name} onChange={v => setEditCustomerForm(f => ({ ...f, name: v }))} placeholder="Full name" />
            <Field label="Mobile Number" value={editCustomerForm.mobile} onChange={v => setEditCustomerForm(f => ({ ...f, mobile: v }))} placeholder="09XX XXX XXXX" />
            <Field label="Email (optional)" value={editCustomerForm.email} onChange={v => setEditCustomerForm(f => ({ ...f, email: v }))} placeholder="email@example.com" />
            <div>
              <label className="label">Municipality</label>
              <select className="input-field" value={editCustomerForm.municipality} onChange={e => setEditCustomerForm(f => ({ ...f, municipality: e.target.value }))}>
                {MUNICIPALITIES.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <button onClick={saveCustomerEdit} className="w-full text-white font-semibold py-3 px-6 rounded-xl transition-all" style={{ backgroundColor: '#1B4F72' }}>
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* Add / Edit Promo Modal */}
      {showAddPromo && (
        <Modal title={editPromo ? 'Edit Promotion' : 'Add Promotion'} onClose={() => { setShowAddPromo(false); setEditPromo(null); }}>
          <div className="space-y-3">
            <Field label="Title *" value={promoForm.title} onChange={v => setPromoForm(f => ({ ...f, title: v }))} placeholder="e.g. FREE Check-up This Month!" />
            <div>
              <label className="label">Description *</label>
              <textarea className="input-field" rows={3} value={promoForm.description} onChange={e => setPromoForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the promo..." />
            </div>
            <div>
              <label className="label">Category</label>
              <select className="input-field" value={promoForm.category} onChange={e => setPromoForm(f => ({ ...f, category: e.target.value }))}>
                {['Service', 'Parts', 'Rewards', 'Upgrade', 'Others'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <Field label="Valid Until" type="date" value={promoForm.validUntil} onChange={v => setPromoForm(f => ({ ...f, validUntil: v }))} />
            <button onClick={savePromo} className="w-full text-white font-semibold py-3 px-6 rounded-xl transition-all" style={{ backgroundColor: '#1B4F72' }}>
              {editPromo ? 'Update Promo' : 'Add Promo'}
            </button>
          </div>
        </Modal>
      )}


      {showAddCustomer && (
        <Modal title="Add New Customer" onClose={() => setShowAddCustomer(false)}>
          <div className="space-y-3">
            <Field label="Full Name *" value={customerForm.name} onChange={v => setCustomerForm(f => ({ ...f, name: v }))} placeholder="Full name" />
            <Field label="Mobile Number *" value={customerForm.mobile} onChange={v => setCustomerForm(f => ({ ...f, mobile: v }))} placeholder="09XX XXX XXXX" />
            <div>
              <label className="label">Municipality</label>
              <select className="input-field" value={customerForm.municipality} onChange={e => setCustomerForm(f => ({ ...f, municipality: e.target.value }))}>
                {MUNICIPALITIES.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <Field label="Email (optional)" value={customerForm.email} onChange={v => setCustomerForm(f => ({ ...f, email: v }))} placeholder="email@example.com" />
            <button onClick={addCustomer} className="btn-primary w-full">Add Customer</button>
          </div>
        </Modal>
      )}

      {/* Add Vehicle Modal */}
      {showAddVehicle && (
        <Modal title="Add Vehicle" onClose={() => setShowAddVehicle(false)}>
          <div className="space-y-3">
            <div>
              <label className="label">Customer *</label>
              <select className="input-field" value={vehicleForm.customerId} onChange={e => setVehicleForm(f => ({ ...f, customerId: e.target.value }))}>
                <option value="">Select customer…</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} · {c.mobile}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Model *</label>
              <select className="input-field" value={vehicleForm.model} onChange={e => setVehicleForm(f => ({ ...f, model: e.target.value }))}>
                <option value="">Select model…</option>
                <option>SevenStars S1</option>
                <option>SevenStars S1V2</option>
                <option>SevenStars S2</option>
                <option>SevenStars S2V2</option>
                <option>SevenStars S3V1</option>
                <option>SevenStars S3V2</option>
                <option>SevenStars S4</option>
                <option>SevenStars S4V1</option>
                <option>SevenStars S5</option>
                <option>SevenStars S5A</option>
                <option>SevenStars S5V2</option>
                <option>SevenStars S6</option>
                <option>SevenStars S7</option>
                <option>SevenStars Wrangler</option>
                <option>SevenStars ZX1</option>
              </select>
            </div>
            <Field label="Serial Number *" value={vehicleForm.serialNumber} onChange={v => setVehicleForm(f => ({ ...f, serialNumber: v }))} placeholder="SS-2024-XXXX" />
            <Field label="Purchase Date" type="date" value={vehicleForm.purchaseDate} onChange={v => setVehicleForm(f => ({ ...f, purchaseDate: v }))} />
            <Field label="Battery Type" value={vehicleForm.batteryType} onChange={v => setVehicleForm(f => ({ ...f, batteryType: v }))} placeholder="e.g. Lithium 48V 20Ah" />
            <Field label="Color" value={vehicleForm.color} onChange={v => setVehicleForm(f => ({ ...f, color: v }))} placeholder="e.g. Matte Black" />
            <button onClick={addVehicle} className="btn-primary w-full">Add Vehicle</button>
          </div>
        </Modal>
      )}

      {/* Add Service Record Modal */}
      {showAddService && (
        <Modal title="Add Service Record" onClose={() => setShowAddService(false)}>
          <div className="space-y-3">
            <div>
              <label className="label">Vehicle *</label>
              <select className="input-field" value={serviceForm.vehicleId} onChange={e => {
                const v = vehicles.find(x => x.id === e.target.value);
                setServiceForm(f => ({ ...f, vehicleId: e.target.value, customerId: v?.customerId || '' }));
              }}>
                <option value="">Select vehicle…</option>
                {vehicles.map(v => {
                  const c = customerStore.getById(v.customerId);
                  return <option key={v.id} value={v.id}>{v.model} · {c?.name || ''}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="label">Service Type *</label>
              <select className="input-field" value={serviceForm.type} onChange={e => setServiceForm(f => ({ ...f, type: e.target.value }))}>
                {SERVICE_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <Field label="Service Date" type="date" value={serviceForm.date} onChange={v => setServiceForm(f => ({ ...f, date: v }))} />
            <Field label="Technician Name" value={serviceForm.technicianName} onChange={v => setServiceForm(f => ({ ...f, technicianName: v }))} placeholder="Technician's name" />
            <div>
              <label className="label">Technician Notes</label>
              <textarea className="input-field" rows={3} value={serviceForm.technicianNotes} onChange={e => setServiceForm(f => ({ ...f, technicianNotes: e.target.value }))} placeholder="What was done?" />
            </div>
            <Field label="Parts Replaced" value={serviceForm.partsReplaced} onChange={v => setServiceForm(f => ({ ...f, partsReplaced: v }))} placeholder="None or list parts" />
            <Field label="Next Service Date" type="date" value={serviceForm.nextServiceDate} onChange={v => setServiceForm(f => ({ ...f, nextServiceDate: v }))} />
            <button onClick={addServiceRecord} className="btn-primary w-full">Save Record</button>
          </div>
        </Modal>
      )}

      {/* QR Code Modal */}
      {qrCustomer && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-xs w-full text-center">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800">Customer QR Code</h3>
              <button onClick={() => setQrCustomer(null)}><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="bg-slate-50 rounded-xl p-2 mb-3">
              <img src={logo} alt="SevenStars" className="h-8 mx-auto mb-2 object-contain" />
              <p className="text-slate-700 font-bold text-sm">{qrCustomer.name}</p>
              <p className="text-slate-400 text-xs">{qrCustomer.municipality}</p>
            </div>
            <div className="bg-white p-3 rounded-2xl border-2 border-brand-100 inline-block mb-4">
              <QRCodeSVG
                value={`${window.location.origin}${window.location.pathname}?customer=${qrCustomer.id}`}
                size={180}
                fgColor="#1B4F72"
              />
            </div>
            <p className="text-slate-500 text-xs mb-4">Customer scans this to open their SmartCare Passport directly — no search needed.</p>
            <button
              onClick={() => {
                window.print();
              }}
              className="btn-primary w-full flex items-center justify-center gap-2 text-sm">
              🖨️ Print / Save QR Code
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-5 py-3 rounded-2xl text-sm shadow-lg z-50">
          {toast}
        </div>
      )}

      {/* Bottom Nav — 6 tabs, compact to fit all on screen */}
      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto border-t z-40" style={{ backgroundColor: '#081d31', borderColor: '#12375a' }}>
        <div className="grid grid-cols-6 px-0 py-1">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex flex-col items-center gap-0.5 py-2 rounded-xl transition-all cursor-pointer ${tab === t.key ? 'bg-slate-800' : 'text-slate-500'}`}
              style={tab === t.key ? { color: '#E87722' } : {}}>
              <t.icon size={17} />
              <span className="text-xs font-medium" style={{ fontSize: '10px' }}>{t.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end overflow-y-auto">
      <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl p-6 mt-20 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800">{title}</h3>
          <button onClick={onClose}><X size={20} className="text-slate-400" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input type={type} className="input-field" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}
